import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, IconButton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import { NumericInput } from '../../Input/NumericInput';
import { RetirementOptions } from '../RetirementPage.hooks';
import { INITIAL_VALUES, schema } from './RetirementForm.hooks';
import styles from './RetirementForm.module.css';

export type RetirementFormProps = {
  initialValues?: Partial<RetirementOptions>;
  onSubmit: (options: RetirementOptions) => void;
  className?: string;
};

const FieldTooltip = ({ text }: { text: string }) => (
  <Tooltip title={text} arrow placement="top">
    <IconButton size="small" sx={{ ml: 0.5, p: 0.25, color: '#9ca3af' }}>
      <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
    </IconButton>
  </Tooltip>
);

export const RetirementForm = ({ initialValues, onSubmit, className }: RetirementFormProps) => {
  const handleSubmit = useCallback((opts: RetirementOptions) => onSubmit(opts), [onSubmit]);
  const mergedInitialValues = { ...INITIAL_VALUES, ...initialValues };

  return (
    <Formik
      initialValues={mergedInitialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ errors, setFieldValue }) => (
        <Form className={classNames(styles.root, className)}>
          <div className={styles.body}>
            <Field name="totalAsset">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      은퇴 시점 보유 자산 (원)
                      <FieldTooltip text="실거주 부동산 제외, 금융자산 합계" />
                    </span>
                  }
                  placeholder="300,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.totalAsset && <div className={styles.error}>{errors.totalAsset}</div>}

            <Field name="monthlyExpense">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      월 생활비 (원)
                      <FieldTooltip text="통계청 기준 노인 부부 평균 월 지출 약 270만원. 여행·의료비 포함 현실적으로 설정" />
                    </span>
                  }
                  placeholder="3,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.monthlyExpense && <div className={styles.error}>{errors.monthlyExpense}</div>}

            <Field name="annualReturnRate">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      은퇴 후 운용 수익률 (%)
                      <FieldTooltip text="은퇴 후 보수적 권장: 3~4%. 낙관적 추정보다 안전마진이 중요합니다." />
                    </span>
                  }
                  placeholder="3.5"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue)}
                />
              )}
            </Field>
            {errors.annualReturnRate && (
              <div className={styles.error}>{errors.annualReturnRate}</div>
            )}

            <Field name="retirementAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      은퇴 나이 (세)
                      <FieldTooltip text="언제 은퇴할 계획인가요? 실제보다 2~3년 앞당겨 시뮬레이션해 보면 안전 마진을 확인할 수 있습니다." />
                    </span>
                  }
                  placeholder="60"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(values) => setFieldValue(field.name, values.floatValue)}
                />
              )}
            </Field>
            {errors.retirementAge && <div className={styles.error}>{errors.retirementAge}</div>}
          </div>
          <div className={styles.action}>
            <Button className={styles.button} variant="contained" type="submit">
              시뮬레이션 시작
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
